// One diagram in one content window (TODO §21/§5, §6).
//
// **At rest it is a static SVG string** from the render cache — cheap, printable, vector,
// and it needs no editor loaded. The live `<Excalidraw>` mounts only for the window being
// edited, behind a `React.lazy` boundary (§10.3), and only inside `DeckEditor`'s
// `DiagramEditingProvider` — so the print layer, the presentation overlay and the
// off-screen thumbnail capturer render the SVG and structurally cannot do anything else
// (§5.1).

import { lazy, Suspense, useEffect, useSyncExternalStore } from "react";
import { Pencil } from "lucide-react";

import { useDiagramEditing } from "@/components/deck/diagramEditing";
import { useScaledBox } from "@/hooks/useScaledBox";
import {
  ensureDiagramSvg,
  peekDiagramSvg,
  subscribeDiagramSvgs,
} from "@/lib/diagramSvg";
import { cn } from "@/lib/utils";
import { diagramDocUpdated } from "@/store/diagramSlice";
import { ownerForContentDoc } from "@/store/ownerSelectors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import type { DiagramDocument } from "@/types/diagram";

const DiagramEditor = lazy(() => import("@/components/deck/DiagramEditor"));

// §5.2 Read the cache for one doc, re-rendering when its entry lands. `useSyncExternalStore`
// rather than an effect + state: the cache is a module singleton written from outside React
// (the HTML export writes to it too), and this is the API for exactly that.
function useDiagramSvg(
  docId: string,
  doc: DiagramDocument | undefined,
  editing: boolean,
) {
  const entry = useSyncExternalStore(
    subscribeDiagramSvgs,
    () => peekDiagramSvg(docId),
    () => null, // no server render in this app; a stable null keeps the hook honest
  );
  useEffect(() => {
    // Not while THIS window is being edited: the editor is what is on screen, so re-rendering
    // the at-rest SVG on every 400ms autosave is work whose result nothing displays — and it
    // notifies every other subscriber in the deck each time it lands. `editing` is in the
    // deps so leaving the session renders once, immediately, with the final scene.
    if (doc && !editing) ensureDiagramSvg(docId, doc);
  }, [docId, doc, editing]);
  return entry;
}

export function DiagramBlock({
  docId,
  readOnly,
}: {
  docId: string;
  readOnly: boolean;
}) {
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.diagram[docId]);
  // §17.2.3 The owner key for anything this scene creates — the deck's, or the NUGGET's when
  // the slide holding it is linked. Resolved from the same selector the persistence listener
  // uses, so a pasted image and the scene it is pasted into can never disagree about who
  // owns them.
  const owner = useAppSelector((s) => ownerForContentDoc(s, docId));
  const {
    selectedDocId,
    select,
    editingDocId,
    beginEdit,
    endEdit,
    setApi,
    noteSceneChange,
    reportWindowSize,
  } = useDiagramEditing();
  const editing = editingDocId === docId;
  const svg = useDiagramSvg(docId, doc, editing);

  // §3.1/§3.4 The window's own LOGICAL size, plus the CSS scale the slide canvas applies to
  // it. The size is what a new diagram's frame is authored against — `clientWidth` /
  // `clientHeight` are layout units, which the transform does not touch, so they are already
  // in the 1600×900 canvas's own units. The scale is what the live editor needs; see
  // `useScaledBox` and the counter-transform below.
  const [boxRef, box] = useScaledBox<HTMLDivElement>();
  useEffect(() => {
    reportWindowSize(docId, { width: box.width, height: box.height });
  }, [docId, box.width, box.height, reportWindowSize]);

  const selected = selectedDocId === docId;
  // §6.2/§6.3 Editing is offered only where an editor could exist at all: an editable window
  // inside the provider. `beginEdit` is the inert no-op outside it, so this is belt and
  // braces on top of a structural guarantee.
  const editable = !readOnly && !!doc;

  if (!doc) {
    // The reference outlived its body — a bundle imported without its diagrams, or a body
    // dropped by a cascade this window has not caught up with. Draw the window, not a crash.
    return (
      <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 text-xs text-slate-400">
        This diagram's drawing is missing.
      </div>
    );
  }

  if (editing) {
    // THE COUNTER-TRANSFORM. A slide paints its logical canvas with `transform: scale(k)`
    // (§1.4), and Excalidraw does its own pointer arithmetic: it sizes its canvas from the
    // container's `clientWidth`/`clientHeight` — layout units, untouched by the transform —
    // but converts pointer events through `getBoundingClientRect()`, which is post-transform
    // screen units. At k = 0.55 a container that lays out as 760 wide is 418 wide on screen,
    // so a click near the right edge arrives as scene x ≈ 418 in a canvas 760 units across.
    // The error is zero at the transform origin and grows with distance from it — shapes
    // appear a couple of hundred pixels up and left of the cursor.
    //
    // The fix is to give the editor a box whose layout size IS its screen size: lay it out at
    // `k ×` the window (so it measures the number of screen pixels it actually occupies) and
    // scale it back by `1 / k` from the same top-left origin (so it still fills the window).
    // Net visual size is unchanged, and Excalidraw's two sources of truth agree again without
    // it knowing anything about slides.
    //
    // Rendering waits for a real measurement: mounting at k = 0 would divide by zero, and
    // mounting at k = 1 and correcting afterwards would leave Excalidraw's own resize
    // observer to reconcile a canvas that changed size under it.
    const ready = box.scale > 0 && box.width > 0 && box.height > 0;
    return (
      <div
        ref={boxRef}
        className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border-2 border-slate-900"
      >
        {ready && (
          <div
            style={{
              width: box.width * box.scale,
              height: box.height * box.scale,
              transform: `scale(${1 / box.scale})`,
              transformOrigin: "top left",
            }}
          >
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                  Loading the drawing tools…
                </div>
              }
            >
              <DiagramEditor
                key={docId}
                docId={docId}
                doc={doc}
                owner={owner}
                onChange={(next) =>
                  dispatch(diagramDocUpdated({ docId, doc: next }))
                }
                onExit={endEdit}
                onApi={setApi}
                onSceneChange={noteSceneChange}
              />
            </Suspense>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={boxRef}
      // §6.2 Single click selects the WINDOW; §6.3 double click (or Enter on the selected
      // window) mounts the editor. Nothing in the app behaved this way before — a Gridd
      // embed is always live — so the affordance has to be visible: a ring when selected and
      // an explicit Edit button, rather than a double-click nobody discovers.
      role={editable ? "button" : undefined}
      tabIndex={editable ? 0 : undefined}
      onClick={editable ? () => select(docId) : undefined}
      onDoubleClick={editable ? () => beginEdit(docId) : undefined}
      onKeyDown={
        editable
          ? (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                beginEdit(docId);
              }
            }
          : undefined
      }
      className={cn(
        "group/diagram relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden rounded-md border bg-white",
        editable && "cursor-pointer",
        selected
          ? "border-slate-900 ring-2 ring-slate-900/20"
          : "border-slate-200",
      )}
    >
      {svg ? (
        // §3.4 The SVG is FITTED into the window (`object-fit: contain` semantics, via the
        // max-* pair below), never re-cropped. A layout change — single → two-h, or a split
        // drag — changes the window's aspect; the frame keeps its authored size and
        // letterboxes. Silently resizing the frame instead would re-crop a drawing the
        // author composed, with no undo affordance (the explicit "Fit frame to window"
        // action in the right panel is the undoable version of that).
        <div
          className="flex h-full w-full items-center justify-center [&>svg]:max-h-full [&>svg]:max-w-full [&>svg]:h-auto [&>svg]:w-auto"
          // The string comes from `exportToSvg` on our own scene data, serialized in this
          // same origin — not from a network response or from user-supplied markup.
          dangerouslySetInnerHTML={{ __html: svg.svg }}
        />
      ) : (
        <span className="text-xs text-slate-400">Rendering the diagram…</span>
      )}

      {editable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            beginEdit(docId);
          }}
          // Top-left: `ContentWindow`'s own Clear button sits top-right, and the image
          // block's Edit overlay already had to shift left to make room for it.
          className="absolute left-2 top-2 z-10 hidden items-center gap-1 rounded bg-white/90 px-2 py-1 text-xs font-medium text-slate-700 shadow group-hover/diagram:flex"
          aria-label="Edit diagram"
          title="Edit diagram"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      )}
    </div>
  );
}
